import React, { useEffect, useState } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    CopyOutlined,
    CheckOutlined,
    SaveOutlined,
    CloseOutlined
} from "@ant-design/icons";
import { Col, Row, message, Button, Input } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { capitalizeFirstLetter } from "../../../../utils/functions";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import CommonDialog from "../../../global/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import Spinner from "../../../global/spinner";

const EnhanceZapierDetails = ({
    zapierAccountDetail,
    setShowDetail,
    keyParams
}) => {
    const [inputType, setInputType] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [destinations, setDestinations] = useState([]);
    const [webhookLinks, setWebhookLinks] = useState({});
    const [copyClipboardTextPassword, setCopyClipboardTextPassword] = useState(false);
    const [copyClipboardTextEmail, setCopyClipboardTextEmail] = useState(false);
    const [destinationPasswordVisible, setDestinationPasswordVisible] = useState({});

    const queryClient = useQueryClient()
    const saveZapierConfigMutation = useGenericMutation({
        endpoint: "zapier",
        onSuccess: () => {
            message.success("Zapier configuration saved successfully");
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.zapier.lists({ ...keyParams }),
                type: "active"
            })
            setShowDetail(false)
        },
        onError: (error) => {
            message.error(error?.data?.error?.message || "Failed to save configuration");
        }
    });

    const saveZapierSetupCompleteMutation = useGenericMutation({
        endpoint: "zapier/zap/setup/completed",
        onSuccess: () => {
            message.success("Zapier status updated successfully");
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.zapier.lists({ ...keyParams }),
                type: "active"
            })
            setShowDetail(false);
        },
        onError: (error) => {
            message.error(error?.response?.data?.message || "Failed to update status");
        }
    });

    useEffect(() => {
        if (zapierAccountDetail?.destinations) {
            setDestinations((prevDestinations) => [
                ...prevDestinations,
                ...zapierAccountDetail.destinations,
            ]);
        }
    }, [zapierAccountDetail?.destinations]);


    useEffect(() => {
        const updatedLinks = {};

        destinations.forEach((des) => {
            updatedLinks[des.name] = des?.webhookUrl;
        });

        setWebhookLinks((prevWebhookLinks) => ({
            ...prevWebhookLinks,
            ...updatedLinks,
        }));
    }, [destinations]);

    const saveZapierSetup = async (destinationName) => {
        try {
            await saveZapierConfigMutation.mutateAsync({
                id: zapierAccountDetail._id,
                email: zapierAccountDetail.email,
                password: zapierAccountDetail.password,
                destinations,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleZapierSetusComplete = async () => {
        try {
            await saveZapierSetupCompleteMutation.mutateAsync({
                id: zapierAccountDetail._id,
                status: !zapierAccountDetail.zapCreatedByAdmin,
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleWebhookLinkChange = (e, destinationName) => {
        setDestinations((prevDestinations) => {
            return prevDestinations.map((destination) => {
                if (destination.name === destinationName) {
                    return {
                        ...destination,
                        webhookUrl: e.target.value,
                    };
                }
                return destination;
            });
        });

        setWebhookLinks({
            ...webhookLinks,
            [destinationName]: e.target.value,
        });
    };

    const togglePassword = () => {
        setInputType(!inputType);
    };

    const toggleDestinationPassword = (idx) => {
        setDestinationPasswordVisible(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }));
    };

    const toggleCopyIcon = (idx, type) => {
        const copyStateSetter =
            type === "password" ? setCopyClipboardTextPassword : setCopyClipboardTextEmail;

        copyStateSetter((prevCopyStates) => {
            return { ...prevCopyStates, [idx]: !prevCopyStates[idx] };
        });
    };

    const copyToClipboard = (text, idx, type) => {
        navigator.clipboard.writeText(text);
        toggleCopyIcon(idx, type);
        message.success("Copied to clipboard");
        setTimeout(() => {
            toggleCopyIcon(idx, type);
        }, 1000);
    };

    const getIconForField = (label) => {
        const iconMap = {
            User: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Password: <EyeOutlined />,
            "Enter Zapier Webhook Link": <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    if (!zapierAccountDetail) return null;

    return (
        <>
            <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
                <Row gutter={[24, 24]}>
                    {/* Account Information - Combined Customer & Zapier Info */}
                    <Col span={24}>
                        <CommonCard
                            title={
                                <div className="flex items-center gap-2">
                                    <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                                    <span style={{ fontSize: "16px", fontWeight: 600 }}>Account Information</span>
                                </div>
                            }
                        >
                            <Row gutter={[24, 0]}>
                                {/* Customer Information Column */}
                                <Col span={12}>
                                    <div className="border-r border-gray-200 pr-6">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                            <UserOutlined style={{ color: "var(--primary-color, #1890ff)" }} />
                                            Customer Details
                                        </h4>
                                        <Row>
                                            {[
                                                { label: "User", value: zapierAccountDetail?.userDetails?.name },
                                                { label: "Email", value: zapierAccountDetail?.userDetails?.email },
                                                { label: "Phone", value: zapierAccountDetail?.userDetails?.phone },
                                            ].map((item, idx) => (
                                                <Col span={24} key={item.label} className={cn("border-b border-gray-200 py-4", idx === 2 && "!border-b-0")}>
                                                    <div className="flex justify-between items-center w-full">
                                                        <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                            <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                                {getIconForField(item.label)}
                                                            </span>
                                                            {item.label}
                                                        </span>
                                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </Col>

                                {/* Zapier Information Column */}
                                <Col span={12}>
                                    <div className="pl-6">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                            <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)" }} />
                                            Zapier Credentials
                                        </h4>
                                        <Row>
                                            {/* Email */}
                                            <Col span={24} className="border-b border-gray-200 py-4">
                                                <div className="flex justify-between items-center w-full">
                                                    <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                        <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                            <MailOutlined />
                                                        </span>
                                                        Email
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-semibold text-gray-900">{zapierAccountDetail?.email}</span>
                                                        {copyClipboardTextEmail[zapierAccountDetail?._id] ? (
                                                            <CheckOutlined className="text-success cursor-pointer" style={{ fontSize: "16px" }} />
                                                        ) : (
                                                            <CopyOutlined
                                                                className="text-success cursor-pointer"
                                                                style={{ fontSize: "16px" }}
                                                                title="Copy Email"
                                                                onClick={() => copyToClipboard(zapierAccountDetail?.email, zapierAccountDetail?._id, "email")}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>

                                            {/* Password */}
                                            <Col span={24} className="py-4">
                                                <div className="flex justify-between items-center w-full">
                                                    <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                        <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                            {inputType ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                                        </span>
                                                        Password
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            className="text-sm font-semibold text-gray-900"
                                                            style={inputType ? { filter: "blur(5px)" } : {}}
                                                        >
                                                            {zapierAccountDetail?.password}
                                                        </span>
                                                        {inputType ? (
                                                            <EyeOutlined
                                                                className="text-primary cursor-pointer"
                                                                style={{ fontSize: "16px" }}
                                                                title="Show Password"
                                                                onClick={togglePassword}
                                                            />
                                                        ) : (
                                                            <EyeInvisibleOutlined
                                                                className="text-primary cursor-pointer"
                                                                style={{ fontSize: "16px" }}
                                                                title="Hide Password"
                                                                onClick={togglePassword}
                                                            />
                                                        )}
                                                        {copyClipboardTextPassword[zapierAccountDetail?._id] ? (
                                                            <CheckOutlined className="text-success cursor-pointer" style={{ fontSize: "16px" }} />
                                                        ) : (
                                                            <CopyOutlined
                                                                className="text-success cursor-pointer"
                                                                style={{ fontSize: "16px" }}
                                                                title="Copy Password"
                                                                onClick={() => copyToClipboard(zapierAccountDetail?.password, zapierAccountDetail?._id, "password")}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </CommonCard>
                    </Col>
                </Row>

                {/* Destination Information Cards */}
                {zapierAccountDetail?.destinations?.map((destination, idx) => (
                    <Row key={idx} gutter={[24, 24]} className="mt-6">
                        <Col span={24}>
                            <CommonCard
                                title={
                                    <div className="flex items-center gap-2">
                                        <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                                        <span style={{ fontSize: "16px", fontWeight: 600 }}>
                                            Customer {capitalizeFirstLetter(destination.name)} Information
                                        </span>
                                    </div>
                                }
                            >
                                <Row>
                                    {/* Email */}
                                    <Col span={24} className="border-b border-gray-200 py-4">
                                        <div className="flex justify-between items-center w-full">
                                            <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                    <MailOutlined />
                                                </span>
                                                Email
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-gray-900">{destination.email}</span>
                                                {copyClipboardTextEmail[idx] ? (
                                                    <CheckOutlined className="text-success cursor-pointer" style={{ fontSize: "16px" }} />
                                                ) : (
                                                    <CopyOutlined
                                                        className="text-success cursor-pointer"
                                                        style={{ fontSize: "16px" }}
                                                        title="Copy Email"
                                                        onClick={() => copyToClipboard(destination.email, idx, "email")}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Password */}
                                    <Col span={24} className="border-b border-gray-200 py-4">
                                        <div className="flex justify-between items-center w-full">
                                            <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                    {destinationPasswordVisible[idx] ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                                </span>
                                                Password
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="text-sm font-semibold text-gray-900"
                                                    style={!destinationPasswordVisible[idx] ? { filter: "blur(5px)" } : {}}
                                                >
                                                    {destination.password}
                                                </span>
                                                {destinationPasswordVisible[idx] ? (
                                                    <EyeInvisibleOutlined
                                                        className="text-primary cursor-pointer"
                                                        style={{ fontSize: "16px" }}
                                                        title="Hide Password"
                                                        onClick={() => toggleDestinationPassword(idx)}
                                                    />
                                                ) : (
                                                    <EyeOutlined
                                                        className="text-primary cursor-pointer"
                                                        style={{ fontSize: "16px" }}
                                                        title="Show Password"
                                                        onClick={() => toggleDestinationPassword(idx)}
                                                    />
                                                )}
                                                {copyClipboardTextPassword[idx] ? (
                                                    <CheckOutlined className="text-success cursor-pointer" style={{ fontSize: "16px" }} />
                                                ) : (
                                                    <CopyOutlined
                                                        className="text-success cursor-pointer"
                                                        style={{ fontSize: "16px" }}
                                                        title="Copy Password"
                                                        onClick={() => copyToClipboard(destination.password, idx, "password")}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Webhook Link Input */}
                                    <Col span={24} className="border-b border-gray-200 py-4">
                                        <div className="flex flex-col gap-2 w-full">
                                            <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                    <InfoCircleOutlined />
                                                </span>
                                                Enter Zapier Webhook Link
                                            </span>
                                            <Input
                                                placeholder="Enter Zapier Webhook Link"
                                                value={webhookLinks[destination.name] || ""}
                                                onChange={(e) => handleWebhookLinkChange(e, destination.name)}
                                                className="w-full focus:!border-primary h-10"
                                            />
                                        </div>
                                    </Col>

                                    {/* Save Button */}
                                    <Col span={24} className="py-4">
                                        <Button
                                            type="default"
                                            icon={saveZapierConfigMutation.isPending ? null : <SaveOutlined />}
                                            onClick={() => saveZapierSetup(destination.name)}
                                            className="w-full h-10 !text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white group"
                                        >
                                            {saveZapierConfigMutation.isPending && <Spinner className={"group-hover:!border-white border-primary"} />}
                                            Save
                                        </Button>
                                    </Col>
                                </Row>
                            </CommonCard>
                        </Col>
                    </Row>
                ))}

                {/* Complete Setup Button */}
                <Row className="mt-6">
                    <Col span={24}>
                        <CommonCard>
                            <Button
                                type="default"
                                icon={zapierAccountDetail?.zapCreatedByAdmin === false ? <CheckOutlined /> : <CloseOutlined />}
                                onClick={() => {
                                    setShowModal(true);
                                }}
                                className={zapierAccountDetail?.zapCreatedByAdmin === false
                                    ? "w-full h-10 !text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white"
                                    : "w-full h-10 border !border-red-500 !text-red-500 hover:!bg-red-500 hover:!text-white border-none hover:!border-none"
                                }
                            >

                                {zapierAccountDetail?.zapCreatedByAdmin === false
                                    ? "Complete Zapier Setup"
                                    : "Mark Pending"}
                            </Button>
                        </CommonCard>
                    </Col>
                </Row>
            </div>

            {showModal && (
                <CommonDialog
                    title={zapierAccountDetail?.zapCreatedByAdmin ? "Mark Pending" : "Zapier Setup Completion"}
                    description={zapierAccountDetail?.zapCreatedByAdmin ? "Are you sure you want to mark this Zapier Setup as pending?" : "Are you sure you want to complete Zapier Setup?"}
                    open={showModal}
                    onCancel={() => setShowModal(false)}
                    onOk={handleZapierSetusComplete}
                    isConfirmButtonLoading={saveZapierConfigMutation.isPending}
                />
            )}
        </>
    );
};

export default EnhanceZapierDetails;